// Predictive search under the header field.
//
// <aps-search> wraps the form the header already had. It fetches the suggestions
// section through Shopify's suggest endpoint as the shopper types and puts the
// markup in the panel under the field; the section does all the drawing, this
// only decides when to ask, what to ask for, and what the keys do.
//
// Decisions it carries, all dated 2026-09-13 in docs/decisions:
//   with nothing typed the panel shows recent searches and nothing else, and
//   with no history it does not open;
//   a query with digits and no spaces is a part number, and a letter O inside
//   its digits is offered back as a zero when the typed form finds nothing;
//   Enter opens the product when exactly one matches the full part number,
//   which the section marks, and submits the form otherwise;
//   one request in flight at a time, the previous one aborted, and a 429 backs
//   off rather than retrying.
//
// One function scope. aps-header.js is a classic script too and declares its own
// MOBILE at the top level; two top-level consts of one name in one page is a
// SyntaxError that stops the whole file, which is how this shipped dead once.
(() => {

const MOBILE = '(max-width: 767px)';
const MIN = 2;
const DEBOUNCE = 150;
const CACHE = 20;
const RECENT_KEY = 'aps-search-recent';
const RECENT_MAX = 8;

const looksLikePart = (s) => !/\s/.test(s) && /\d/.test(s);

// "spx16ooz2" -> "spx1600z2". Each pass fixes an O beside a digit, and a run of
// two needs two passes, so it loops until nothing changes.
const digitsForO = (s) => {
  let out = s;
  let prev;
  do {
    prev = out;
    out = out.replace(/(\d)[oO]/g, '$10').replace(/[oO](\d)/g, '0$1');
  } while (out !== prev);
  return out;
};

class ApsSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-aps-panel]');
    this.status = this.querySelector('[data-aps-status]');
    this.backBtn = this.querySelector('[data-aps-back]');
    this.section = this.dataset.section;
    if (!this.form || !this.input || !this.panel || !this.section) return;

    this.cache = new Map();
    this.seq = 0;
    this.index = -1;
    this.retryAt = 0;
    this.fails = 0;
    this.timer = null;
    this.controller = null;
    this.tagged = false;

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('focus', () => this.onFocus());
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.form.addEventListener('submit', () => this.remember(this.query));
    this.backBtn?.addEventListener('click', () => {
      this.close();
      this.input.blur();
    });
    this.panel.addEventListener('click', (e) => this.onPanelClick(e));

    this.onDocDown = (e) => {
      if (!this.contains(e.target)) this.close();
    };
    this.onDocKey = (e) => {
      if (e.key !== 'Escape' || !this.isOpen) return;
      this.close();
      this.input.blur();
    };
    document.addEventListener('pointerdown', this.onDocDown);
    document.addEventListener('keydown', this.onDocKey);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onDocDown);
    document.removeEventListener('keydown', this.onDocKey);
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  get isMobile() {
    return window.matchMedia(MOBILE).matches;
  }

  get query() {
    return this.input.value.trim();
  }

  get rows() {
    return [...this.panel.querySelectorAll('[role="option"]')];
  }

  route(name, fallback) {
    const routes = window.Theme && window.Theme.routes;
    return (routes && routes[name]) || fallback;
  }

  say(text) {
    if (this.status) this.status.textContent = text;
  }

  // -- asking ----------------------------------------------------------------

  onInput() {
    clearTimeout(this.timer);
    const q = this.query;
    if (q.length < MIN) {
      this.abort();
      if (q.length === 0) this.showRecent();
      else this.close();
      return;
    }
    this.timer = setTimeout(() => this.search(q), DEBOUNCE);
  }

  onFocus() {
    const q = this.query;
    if (q.length >= MIN) this.search(q);
    else if (q.length === 0) this.showRecent();
  }

  abort() {
    this.controller?.abort();
    this.controller = null;
  }

  async search(q) {
    if (Date.now() < this.retryAt) return;
    const token = ++this.seq;
    const cached = this.cache.get(q);
    if (cached !== undefined) {
      this.render(cached, q);
      return;
    }
    this.abort();
    const controller = new AbortController();
    this.controller = controller;
    const html = await this.fetchSection(q, controller.signal);
    if (token !== this.seq || html === null) return;
    this.cache.set(q, html);
    if (this.cache.size > CACHE) this.cache.delete(this.cache.keys().next().value);
    this.render(html, q);
  }

  async fetchSection(q, signal) {
    const url = new URL(this.route('predictive_search_url', '/search/suggest'), location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('section_id', this.section);
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    try {
      const r = await fetch(url, { signal });
      if (r.status === 429) {
        this.fails += 1;
        this.retryAt = Date.now() + Math.min(30000, 1000 * 2 ** this.fails);
        return null;
      }
      if (!r.ok) return null;
      this.fails = 0;
      const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const results = doc.querySelector('[data-aps-results]');
      return results ? results.outerHTML : '';
    } catch (e) {
      // Aborted by a newer keystroke, or offline. Nothing to show and nothing to say.
      return null;
    }
  }

  // -- showing ---------------------------------------------------------------

  render(html, q) {
    if (!html) {
      this.close();
      return;
    }
    this.panel.innerHTML = html;
    this.highlight(q);
    this.selectRow(-1);
    this.open();
    const results = this.panel.querySelector('[data-aps-results]');
    const products = Number(results?.dataset.products || 0);
    const collections = Number(results?.dataset.collections || 0);
    const n = this.rows.length;
    if (products === 0 && collections === 0) this.say(this.dataset.statusNone || '');
    else this.say((this.dataset.statusSome || '{count} suggestions').replace('{count}', n));
    this.tagClarity();
    if (products === 0) this.didYouMean(q);
  }

  // The typed form found no products. If it reads as a part number with a letter O
  // where a zero belongs, ask for that form, and offer it only if it finds something.
  async didYouMean(q) {
    if (!looksLikePart(q)) return;
    const stripped = q.replace(/[\s-]+/g, '');
    const alt = digitsForO(stripped);
    if (alt === stripped) return;
    const slot = this.panel.querySelector('[data-aps-dym]');
    const link = slot?.querySelector('[data-aps-dym-link]');
    if (!slot || !link) return;
    const token = this.seq;
    const html = await this.fetchSection(alt, new AbortController().signal);
    if (token !== this.seq || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (Number(doc.querySelector('[data-aps-results]')?.dataset.products || 0) === 0) return;
    link.href = `${this.route('search_url', '/search')}?q=${encodeURIComponent(alt)}&type=product&options%5Bprefix%5D=last`;
    link.textContent = (link.dataset.label || '{query}').replace('{query}', alt);
    slot.hidden = false;
  }

  // The matched part of a part number, in blue, the way the mockup draws it. The
  // section cannot do this: Liquid has no case-insensitive find.
  highlight(q) {
    const needle = q.toLowerCase();
    if (!needle) return;
    for (const el of this.panel.querySelectorAll('[data-aps-pn]')) {
      const text = el.textContent;
      const i = text.toLowerCase().indexOf(needle);
      if (i < 0) continue;
      const mark = document.createElement('mark');
      mark.textContent = text.slice(i, i + needle.length);
      el.replaceChildren(text.slice(0, i), mark, text.slice(i + needle.length));
    }
  }

  selectRow(i) {
    const rows = this.rows;
    this.index = i;
    rows.forEach((row, k) => {
      if (k === i) {
        row.setAttribute('aria-selected', 'true');
        row.scrollIntoView({ block: 'nearest' });
      } else {
        row.removeAttribute('aria-selected');
      }
    });
    const row = i >= 0 ? rows[i] : null;
    if (row) {
      if (!row.id) row.id = `aps-search-opt-${i}`;
      this.input.setAttribute('aria-activedescendant', row.id);
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onKeydown(e) {
    if (!this.isOpen) {
      if (e.key === 'ArrowDown' && this.query.length === 0) this.showRecent();
      return;
    }
    const n = this.rows.length;
    if (e.key === 'ArrowDown' && n) {
      e.preventDefault();
      this.selectRow((this.index + 1) % n);
    } else if (e.key === 'ArrowUp' && n) {
      e.preventDefault();
      this.selectRow((this.index - 1 + n) % n);
    } else if (e.key === 'Enter') {
      const row = this.index >= 0 ? this.rows[this.index] : this.panel.querySelector('[data-aps-exact]');
      if (row && row.href) {
        e.preventDefault();
        this.remember(this.query);
        location.href = row.href;
      }
      // Otherwise the form submits, and the submit handler remembers the query.
    } else if (e.key === 'Tab') {
      this.close();
    }
  }

  onPanelClick(e) {
    if (e.target.closest('[data-aps-recent-clear]')) {
      e.preventDefault();
      this.clearRecent();
      this.close();
      this.input.focus();
      return;
    }
    const row = e.target.closest('a');
    if (row) this.remember(this.query || row.dataset.q || '');
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    if (this.isMobile && !this.classList.contains('open')) {
      this.classList.add('open');
      if (this.backBtn) this.backBtn.hidden = false;
      document.body.style.overflow = 'hidden';
    }
  }

  close() {
    if (!this.isOpen && !this.classList.contains('open')) return;
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.selectRow(-1);
    if (this.classList.contains('open')) {
      this.classList.remove('open');
      if (this.backBtn) this.backBtn.hidden = true;
      document.body.style.overflow = '';
    }
    this.say('');
  }

  // -- recent searches, this browser only --------------------------------------

  readRecent() {
    try {
      const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(v) ? v.filter((x) => x && typeof x.q === 'string' && typeof x.t === 'number') : [];
    } catch (e) {
      return [];
    }
  }

  writeRecent(list) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      // Private mode or storage blocked: recent searches are a convenience, not a feature.
    }
  }

  remember(q) {
    q = (q || '').trim();
    if (q.length < MIN) return;
    const list = this.readRecent().filter((x) => x.q.toLowerCase() !== q.toLowerCase());
    list.unshift({ q, t: Date.now() });
    this.writeRecent(list);
  }

  clearRecent() {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch (e) {
      // Nothing to clear if nothing could be stored.
    }
  }

  ago(t) {
    const days = Math.floor((Date.now() - t) / 86400000);
    if (days <= 0) return this.dataset.agoToday || '';
    if (days === 1) return this.dataset.agoYesterday || '';
    if (days < 7) return (this.dataset.agoDays || '').replace('{count}', days);
    return this.dataset.agoEarlier || '';
  }

  showRecent() {
    const list = this.readRecent();
    const tpl = this.querySelector('[data-aps-recent-tpl]');
    const rowTpl = this.querySelector('[data-aps-recent-row]');
    if (!list.length || !tpl || !rowTpl) {
      this.close();
      return;
    }
    const frag = tpl.content.cloneNode(true);
    const holder = frag.querySelector('[data-aps-recent-rows]');
    const base = this.route('search_url', '/search');
    for (const { q, t } of list) {
      const row = rowTpl.content.cloneNode(true).firstElementChild;
      row.href = `${base}?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last`;
      row.dataset.q = q;
      row.querySelector('.ti').textContent = q;
      row.querySelector('.rm').textContent = this.ago(t);
      holder.append(row);
    }
    this.panel.replaceChildren(frag);
    this.selectRow(-1);
    this.open();
    this.say('');
  }

  // One tag per session, so recordings of sessions that searched can be filtered.
  tagClarity() {
    if (this.tagged) return;
    this.tagged = true;
    try {
      if (typeof window.clarity === 'function') window.clarity('set', 'search', 'used');
    } catch (e) {
      // Clarity is a third party and may not be there.
    }
  }
}

if (!customElements.get('aps-search')) customElements.define('aps-search', ApsSearch);

})();
